import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search } from "lucide-react";
import { TagChip } from "./bits";
import { ECOSYSTEM, TRACK_TAGS } from "../lib/tagMeta";

const TRACKS = [
  { k: "all", label: "All" },
  { k: "AI", label: "AI" },
  { k: "Web3", label: "Web3" },
  { k: "both", label: "AI + Web3" },
];

export default function FilterSheet({ open, onClose, tags, filters, setFilters, resultCount }) {
  const [local, setLocal] = React.useState(filters);
  React.useEffect(() => { if (open) setLocal(filters); }, [open, filters]);

  const ordered = React.useMemo(() => {
    const topic = tags.filter((t) => !TRACK_TAGS.includes(t.tag)).sort((a, b) => {
      const ea = ECOSYSTEM.includes(a.tag), eb = ECOSYSTEM.includes(b.tag);
      if (ea !== eb) return ea ? -1 : 1;
      return b.count - a.count;
    });
    return topic;
  }, [tags]);

  const toggleTag = (tag) => setLocal((f) => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter((t) => t !== tag) : [...f.tags, tag] }));

  const apply = () => { setFilters(local); onClose(); };
  const clear = () => { const cleared = { track: "all", tags: [], q: "" }; setLocal(cleared); setFilters(cleared); onClose(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[50] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="filter-sheet">
          <div className="absolute inset-0 bg-navy-950/60" onClick={onClose} />
          <motion.div className="scroll-area relative z-10 flex max-h-[88vh] w-full max-w-[520px] flex-col overflow-hidden rounded-t-sheet bg-surface-50 shadow-sheet sm:rounded-sheet"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 360, damping: 34 }}>
            <button onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded-full bg-white/80 p-1.5 text-ink-600 shadow-soft"><X size={18} /></button>
            <div className="scroll-area overflow-y-auto p-5 pt-6">
              <h2 className="mb-4 font-display text-[22px] font-bold text-navy-900">Find your kind of project</h2>

              <div className="relative mb-5">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input data-testid="filter-search" className="w-full rounded-md border border-line-200 bg-white py-2.5 pl-9 pr-3 text-[15px] text-ink-900 outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/30"
                  placeholder="Search projects, teams or tech" value={local.q} onChange={(e) => setLocal((f) => ({ ...f, q: e.target.value }))} />
              </div>

              <p className="mb-2 text-[13px] font-bold text-navy-900">Track</p>
              <div className="mb-5 flex flex-wrap gap-2">
                {TRACKS.map((t) => (
                  <button key={t.k} data-testid={`track-${t.k}`} onClick={() => setLocal((f) => ({ ...f, track: t.k }))}
                    className={`rounded-pill border px-4 py-1.5 text-[13px] font-bold ${local.track === t.k ? "border-gold-500 bg-gold-500 text-navy-900" : "border-line-200 bg-white text-ink-900"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              <p className="mb-2 text-[13px] font-bold text-navy-900">U-tags</p>
              <div className="mb-5 flex flex-wrap gap-2">
                {ordered.map((t) => (
                  <TagChip key={t.tag} tag={t.tag} selected={local.tags.includes(t.tag)} onClick={() => toggleTag(t.tag)} />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 border-t border-line-200 bg-white p-4">
              <button onClick={clear} data-testid="filter-clear" className="rounded-pill px-4 py-3 font-semibold text-ink-600">Clear all</button>
              <button onClick={apply} data-testid="filter-apply" className="h-[52px] flex-1 rounded-pill bg-gold-500 font-display text-[16px] font-bold text-navy-900">
                Show {resultCount} projects
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
